import { Elements } from '@/components/elements';
import DoodleLine from '@icons/DoodleLine.svg';
import clsx from 'clsx';
import Image from 'next/image';

export type ThePivotScriptCard = {
    iconSrc?: string;
    iconAlt?: string;
    title?: string;
    description?: string;
    listItems?: string[];
};

export type ThePivotScriptProps = {
    eyebrow?: string;
    title: string;
    description: string;
    cards: ThePivotScriptCard[];
    className?: string;
    contentClassName?: string;
    leftColumnClassName?: string;
    cardsGridClassName?: string;
    doodleClassName?: string;
};

export function ThePivotScript({
    eyebrow = 'SOLUTION',
    title,
    description,
    cards,
    className,
    contentClassName,
    leftColumnClassName,
    cardsGridClassName,
    doodleClassName,
}: ThePivotScriptProps) {
    return (
        <section
            className={clsx(
                'relative overflow-hidden px-5 py-16 md:px-10 md:py-16 lg:px-[79px] bg-brand-900 lg:py-20',
                className,
            )}
        >
            <Image
                src={DoodleLine}
                alt=""
                width={1440}
                height={838}
                className={clsx(
                    'pointer-events-none select-none',
                    'absolute bottom-[-60%] left-[-10%] z-0 max-h-none max-w-none',
                    'h-[838px] w-[1440px]',
                    'rounded-bl-[40px]',
                    'md:bottom-[-48%] md:left-1/2 md:-translate-x-1/2',
                    'lg:bottom-[-35%] lg:left-[-20%] lg:rotate-15 lg:translate-x-0',
                    doodleClassName,
                )}
                aria-hidden
            />
            <div
                className={clsx(
                    'relative w-full overflow-hidden rounded-[40px] ',
                    'p-4 md:px-10 md:py-16 lg:p-16',
                )}
            >

                <div
                    className={clsx(
                        'relative z-10 flex flex-col gap-8 md:gap-10',
                        'lg:flex-row lg:items-start lg:justify-between lg:gap-12',
                        contentClassName,
                    )}
                >
                    <div
                        className={clsx(
                            'flex w-full shrink-0 flex-col gap-4 text-left',
                            'lg:max-w-[560px] lg:sticky lg:top-8',
                            leftColumnClassName,
                        )}
                    >
                        <Elements.Title
                            title={title}
                            type="h2"
                            className="max-w-[560px] text-3xl text-gray-50 md:text-[32px] lg:text-6xl"
                        />
                        <Elements.Description
                            description={description}
                            type="medium"
                            className="max-w-[560px] text-gray-50 md:text-lg"
                        />
                    </div>
                    <div
                        className={clsx(
                            'grid w-full min-w-0 grid-cols-1 gap-4 md:grid-cols-2 md:gap-5',
                            'lg:max-w-[560px] lg:grid-cols-1 lg:shrink-0',
                            cardsGridClassName,
                        )}
                    >
                        {cards.map((card, index) => (
                            <div
                                key={`${card.title}-${index}`}
                                className="relative flex min-h-0 h-full w-full flex-col rounded-2xl"
                            >
                                <div
                                    aria-hidden
                                    className="pointer-events-none absolute inset-0 rounded-2xl"
                                    style={{
                                        padding: 1,
                                        borderRadius: '1rem',
                                        background:
                                            'linear-gradient(135deg, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.06) 42%, rgba(133,191,248,0.5) 100%)',
                                        WebkitMask:
                                            'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                        WebkitMaskComposite: 'xor',
                                        mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                        maskComposite: 'exclude',
                                    }}
                                />
                                <div
                                    className={clsx(
                                        'relative z-10 flex min-h-0 h-full w-full flex-1 flex-col gap-3 rounded-2xl bg-[#0077DF1F] p-4',
                                        'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18)]',
                                    )}
                                >
                                    <div className="flex w-fit items-center gap-2">
                                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#0077DF1F]">
                                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-50 shadow-[0px_0px_0px_2px_#FFFFFF40]">
                                                {cards.indexOf(card) + 1}
                                            </div>
                                        </div>

                                        <Elements.Title
                                            title={card.title}
                                            type="h5"
                                            className="min-w-0 flex-1 text-left text-lg! font-medium text-gray-50 md:text-lg!"
                                        />
                                    </div>
                                    <Elements.Description
                                        description={card.description || ''}
                                        type="small"
                                        className="w-full text-left text-[#FFFFFFB2] md:text-lg"
                                    />
                                    {card.listItems?.length ? (
                                        <ul
                                            className={clsx(
                                                'w-full list-disc space-y-1.5 pl-5 text-left text-sm leading-snug text-gray-50',
                                                'marker:text-gray-300',
                                            )}
                                        >
                                            {card.listItems.map((item, itemIndex) => (
                                                <li
                                                    key={`${card.title}-li-${itemIndex}`}
                                                    className="text-base text-gray-50"
                                                >
                                                    {item}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
